import { type ReactNode, useState, useEffect } from 'react';
import type { CSSProperties } from 'react';
import LoadingSpinner from 'components/loading-spinner';

export interface HydrationGuardProps {
  /** Content to render (hidden until hydrated) */
  children: ReactNode;
  /** Custom loading component (default: LoadingSpinner) */
  loader?: ReactNode;
  /** Background color for the loading overlay (default: #fff) */
  backgroundColor?: string;
  /** Minimum height for the container (default: 100vh) */
  minHeight?: string | number;
  /** Fade transition duration in ms (default: 200) */
  transitionDuration?: number;
  /** Fallback timeout in ms - forces hydration if RAF doesn't fire (default: 3000) */
  fallbackTimeout?: number;
  /** Test ID for testing */
  testId?: string;
}

/**
 * HydrationGuard - Prevents "flash of unstyled/broken content" during React hydration.
 * 
 * ## The Problem This Solves
 * 
 * In Next.js SSR, the page lifecycle is:
 * 
 * 1. **Server renders HTML** → User sees static HTML immediately (fast!)
 * 2. **Browser downloads JS** → HTML is visible but not interactive
 * 3. **React "hydrates"** → React attaches event handlers to existing HTML
 * 4. **Page is interactive** → User can click, type, etc.
 * 
 * The problem: During step 2-3, the page can look broken:
 * - Images may not be loaded yet
 * - CSS-in-JS styles may not be applied
 * - Client-only components render differently than SSR
 * - Layout shifts as content loads
 * 
 * ## How HydrationGuard Works
 * 
 * ```
 * INITIAL STATE (SSR + early client):
 * ┌─────────────────────────────┐
 * │      Loading Spinner        │  ← Overlay (opacity: 1, visible)
 * │         (visible)           │
 * ├─────────────────────────────┤
 * │      Actual Content         │  ← Content (opacity: 0, hidden but RENDERED)
 * │    (hidden, but in DOM)     │     This reserves the correct layout space
 * └─────────────────────────────┘
 * 
 * AFTER HYDRATION:
 * ┌─────────────────────────────┐
 * │      Actual Content         │  ← Content fades in (opacity: 1)
 * │        (visible)            │
 * ├─────────────────────────────┤
 * │      Loading Spinner        │  ← Overlay fades out (opacity: 0)
 * │    (hidden, pointer-events: none)
 * └─────────────────────────────┘
 * ```
 * 
 * ## Why Double requestAnimationFrame?
 * 
 * We need to know when content is actually PAINTED to screen, not just in the DOM.
 * 
 * - `useEffect` fires after React commits to DOM (but before paint)
 * - First `requestAnimationFrame` waits for browser to be ready to paint
 * - Second `requestAnimationFrame` ensures the paint actually happened
 * 
 * This is a well-known pattern for detecting "paint complete" in browsers.
 * 
 * ## Why the Fallback Timeout?
 * 
 * If the main thread is blocked (heavy JS execution), RAF callbacks get delayed.
 * The timeout ensures we don't show a spinner forever - after 3s, we reveal
 * content anyway (better to show potentially-incomplete content than infinite loading).
 * 
 * @example
 * ```tsx
 * // Wrap a section that has client-only behavior
 * <HydrationGuard minHeight={400}>
 *   <ImageGallery />  // Has client-side image loading logic
 * </HydrationGuard>
 * ```
 */
export function HydrationGuard({
  children,
  loader,
  backgroundColor = '#fff',
  minHeight = '100vh',
  transitionDuration = 200,
  fallbackTimeout = 3000,
  testId = 'hydration-guard',
}: HydrationGuardProps) {
  // Start as false - we're not hydrated yet
  // This is always false on SSR (useEffect doesn't run on server)
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    // This effect only runs on the CLIENT after React hydrates the component.
    // On the server, useEffect never runs, so isHydrated stays false.
    
    let rafId: number;
    let timeoutId: ReturnType<typeof setTimeout>;
    
    const markHydrated = () => {
      if (!isHydrated) {
        setIsHydrated(true);
      }
    };

    // Double requestAnimationFrame pattern:
    // - First RAF: Scheduled for next frame (browser is ready to paint)
    // - Second RAF: Scheduled for frame AFTER that (paint has completed)
    // This ensures content is actually visible before we hide the spinner.
    rafId = requestAnimationFrame(() => {
      requestAnimationFrame(markHydrated);
    });
    
    // Safety net: If RAF is blocked (heavy main thread), don't wait forever.
    // After fallbackTimeout, show content anyway.
    timeoutId = setTimeout(() => {
      console.debug('[HydrationGuard] Fallback timeout triggered - RAF may be blocked');
      markHydrated();
    }, fallbackTimeout);

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [fallbackTimeout, isHydrated]);

  // Container holds both content and overlay, stacked via position: relative/absolute
  const containerStyle: CSSProperties = {
    position: 'relative',
    minHeight, // Ensures container has height even before content is visible
  };

  // Content is ALWAYS rendered (for SEO and to reserve layout space)
  // but hidden via opacity until hydration completes
  const contentStyle: CSSProperties = {
    opacity: isHydrated ? 1 : 0,
    visibility: isHydrated ? 'visible' : 'hidden',
    transition: `opacity ${transitionDuration}ms ease-out, visibility ${transitionDuration}ms ease-out`,
  };

  // Overlay sits on TOP of content (z-index: 100) and shows the spinner
  // Fades out when hydration completes
  const overlayStyle: CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor,
    zIndex: 100,
    opacity: isHydrated ? 0 : 1,
    visibility: isHydrated ? 'hidden' : 'visible',
    transition: `opacity ${transitionDuration}ms ease-out, visibility ${transitionDuration}ms ease-out`,
    pointerEvents: isHydrated ? 'none' : 'auto', // Allow clicks through when hidden
  };

  return (
    <div style={containerStyle} data-testid={testId}>
      {/* Content layer - rendered but invisible until hydrated */}
      <div style={contentStyle} data-testid={`${testId}-content`}>
        {children}
      </div>

      {/* Overlay layer - spinner visible until hydrated, then fades out */}
      <div style={overlayStyle} data-testid={`${testId}-overlay`} aria-hidden={isHydrated}>
        {loader || <LoadingSpinner />}
      </div>
    </div>
  );
}

export default HydrationGuard;
